import { request } from './client'
import type { StaffAuth } from './teaching'

/**
 * T7 — the class register.
 *
 * 🔴 THE MARKING ALREADY EXISTED AND WAS CHILD-AT-A-TIME.
 * `markAbsentToday` / `unmarkAbsentToday` in `student.ts` post to
 * `/players/:playerId/absent`, and the app called them from exactly one place:
 * a single child's detail page. Taking a register for twenty-one children meant
 * opening twenty-one screens. Those two calls stay — a teacher correcting one
 * child from their own page is a real thing — and these two are the class shape.
 */

export interface AttendanceRow {
  playerId: string
  rollNumber: string | null
  name: string
  parentName?: string | null
  absent: boolean
  /**
   * 🔴 WHETHER MARKING THIS CHILD ABSENT WILL ACTUALLY TELL ANYBODY.
   *
   * False when no parent is linked, the parent is deactivated, they have no
   * phone, or they have not opted into WhatsApp. A teacher reasonably assumes
   * marking absent notifies the parent; for these children nothing is sent, and
   * the register is the one screen where that is actionable — the fix is a
   * roster linkage the school controls.
   */
  parentReachable: boolean
  /**
   * Whether any parent is linked. With `parentReachable` false, this tells
   * "no parent linked" (the school's roster to fix) apart from "the parent has
   * not turned on WhatsApp alerts" (theirs).
   */
  parentLinked: boolean
}

export interface AttendanceView {
  classId: string
  className: string
  /** `YYYY-MM-DD` — the school's own day, decided by the server. */
  day: string
  rows: AttendanceRow[]
}

export interface SaveAttendanceResult extends AttendanceView {
  newlyAbsent: number
  newlyPresent: number
  /** Outbox rows written. Delivery is the worker's problem. */
  notified: number
  /** Newly absent, but nobody could be told. */
  notNotified: number
}

export const getAttendance = (classId: string, auth: StaffAuth = {}): Promise<AttendanceView> =>
  request<AttendanceView>(`/classes/${encodeURIComponent(classId)}/attendance`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * Save the whole register.
 *
 * 🔴 THE WHOLE SET, NOT A DIFF, AND NOT ONE TAP PER CHILD.
 *
 * The server reconciles today to match what arrives — adding what is new,
 * removing what is gone — and enqueues a WhatsApp message only for children who
 * were **not already** absent. That is what makes a mis-tap harmless: a teacher
 * who marks the wrong row and corrects it before saving has told nobody, and a
 * message cannot be recalled. A per-tap endpoint would have sent it.
 */
export const saveAttendance = (
  classId: string,
  absentPlayerIds: readonly string[],
  auth: StaffAuth = {},
): Promise<SaveAttendanceResult> =>
  request<SaveAttendanceResult>(`/classes/${encodeURIComponent(classId)}/attendance`, {
    method: 'POST',
    body: { absentPlayerIds: [...absentPlayerIds] },
    context: 'user',
    token: auth.token,
  })

/** One child's month. */
export interface MonthChildRow {
  playerId: string
  name: string
  rollNumber: string | null
  /** `YYYY-MM-DD` for each day marked absent, ascending. */
  absentDays: string[]
}

/** One day of the month. */
export interface MonthDay {
  /** `YYYY-MM-DD`, UTC. */
  day: string
  absent: number
  /**
   * 🔴 THERE ARE MARKS FOR THIS DAY — **NOT** "a register was taken".
   *
   * `Absence` stores absences only, so a day every child was present records
   * nothing and arrives here identical to a Sunday. A screen must draw a
   * `recorded: false` day as blank, never as green: filling it in as "all
   * present" invents a register nobody took.
   */
  recorded: boolean
}

export interface MonthAttendance {
  classId: string
  className: string
  /** `YYYY-MM`. */
  month: string
  childCount: number
  /** Every day of the month, ascending, zeroes included. */
  days: MonthDay[]
  children: MonthChildRow[]
  /** 🔴 What the numbers mean, shipped with them — OD-39/OD-72. */
  basis: string
}

/**
 * One calendar month of a class register — read only.
 *
 * 🔴 `getAttendance` IS TODAY AND ONLY TODAY, which is why this exists. Nothing
 * in the product could answer *"how has this child's attendance been this
 * term"* — the question a teacher is asked at every parents' evening.
 *
 * ⚠️ THERE IS NO WRITE COUNTERPART, AND THERE MUST NOT BE. `saveAttendance`
 * takes its day from the SERVER, because a caller-supplied date would let one
 * request rewrite a week of attendance and message a parent about a day that
 * has already passed. Reading a month does not weaken that.
 *
 * ⚠️ A MALFORMED `month` IS ANSWERED WITH THIS MONTH, not a 400 — it is a
 * navigation control, and a mistyped value should draw a calendar.
 */
export const getAttendanceMonth = (
  classId: string,
  month: string,
  auth: StaffAuth = {},
): Promise<MonthAttendance> =>
  request<MonthAttendance>(
    `/classes/${encodeURIComponent(classId)}/attendance/month?month=${encodeURIComponent(month)}`,
    { context: 'user', token: auth.token, signal: auth.signal },
  )
